import { useMemo, useState, type ReactNode } from 'react'
import { Eye, RotateCcw, Settings, ShieldCheck } from 'lucide-react'
import { FloatInput } from '@/components/ui/FloatInput'
import type { HrSettingsData, HrSettingsField } from '@/types/hr'

/**
 * hr2-v2 (E3/E12): HR-Einstellungsansicht — eigene Komponente mit geteilten
 * UI-Bausteinen im Stil der TeacherSettingsView (keine Generalisierung der
 * Lehrer-Ansicht, kein Regression-Risiko).
 *
 * Gleiche Daten-Pipeline für echte HR-Ansicht (editable) und Superadmin-
 * Vorschau (read-only, gespeicherter Zustand) — die Vorschau kann nicht
 * von der Realität driften (E8-Prinzip).
 */

function choiceLabel(field: HrSettingsField, value: string): string {
  return field.choice_labels?.[value] ?? value.replace(/_/g, ' ')
}

function SettingRow({ label, description, hint, readonly, children }: {
  label: string
  description?: string
  hint?: ReactNode
  readonly?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex-1 min-w-0 pr-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-800">{label}</span>
          {readonly && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded">
              <Eye className="w-3 h-3" /> Kein Bearbeitungsrecht
            </span>
          )}
        </div>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        {hint}
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        {children}
      </div>
    </div>
  )
}

function BoolValueBadge({ value }: { value: boolean }) {
  return value
    ? <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">Ja</span>
    : <span className="text-xs text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded">Nein</span>
}

export interface HRSettingsViewProps {
  data: HrSettingsData
  /** false = read-only preview (Superadmin), true = HR live view */
  editable: boolean
  /** Gepufferte Änderungen (nur editable): {key: neuerWert} */
  overrides?: Record<string, unknown>
  onUpdateOverride?: (key: string, value: unknown) => void
  onResetKey?: (key: string) => void
  extraSections?: { name: string; node: ReactNode }[]
}

export function HRSettingsView({
  data,
  editable,
  overrides = {},
  onUpdateOverride,
  onResetKey,
  extraSections = [],
}: HRSettingsViewProps) {
  const fieldsByKey = useMemo(() => {
    const map: Record<string, HrSettingsField> = {}
    for (const f of data.fields) map[f.key] = f
    return map
  }, [data.fields])

  const [internalCategory, setInternalCategory] = useState<string>('')
  const firstNav = extraSections[0]?.name || data.categories[0]?.name || ''
  const activeCategory = internalCategory || firstNav

  const scrollToCard = (name: string) => {
    setInternalCategory(name)
    document.querySelector(`[data-subgroup="${CSS.escape(name)}"]`)?.scrollIntoView({ behavior: 'smooth' })
  }

  const valueOf = (key: string): unknown =>
    key in overrides ? overrides[key] : data.values[key]

  const renderField = (field: HrSettingsField) => {
    const value = valueOf(field.key)
    const overridden = field.key in overrides
    const canEdit = editable && field.editable
    const resetBtn = overridden && onResetKey ? (
      <button onClick={() => onResetKey(field.key)} className="text-xs text-gray-400 hover:text-gray-600" title="Änderung zurücknehmen">
        <RotateCcw className="w-3 h-3" />
      </button>
    ) : null

    // ---------- read-only rendering ----------
    if (!canEdit) {
      if (field.type === 'bool') return <BoolValueBadge value={Boolean(value)} />
      if (field.type === 'enum_list') {
        const list = Array.isArray(value) ? value : []
        if (list.length === 0) return <span className="text-xs text-gray-500">alle</span>
        return (
          <span className="text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
            {list.map(v => choiceLabel(field, String(v))).join(', ')}
          </span>
        )
      }
      if (field.type === 'str' && field.choices) {
        return <span className="text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{choiceLabel(field, String(value ?? '—'))}</span>
      }
      if (Array.isArray(value)) return <span className="text-sm text-gray-600">{value.join(', ')}</span>
      return <span className="text-sm text-gray-700">{value === null || value === undefined ? '—' : String(value)}</span>
    }

    // ---------- editable rendering ----------
    if (field.type === 'bool') {
      const on = Boolean(value)
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onUpdateOverride?.(field.key, !on)}
            role="switch" aria-checked={on} aria-label={field.label}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${on ? 'bg-indigo-600' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
          {resetBtn}
        </div>
      )
    }

    if (field.type === 'enum_list') {
      const list = Array.isArray(value) ? (value as string[]) : []
      const choices = field.choices ?? []
      return (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            {choices.map(c => {
              const on = list.includes(c)
              return (
                <button
                  key={c} type="button"
                  onClick={() => onUpdateOverride?.(field.key, on ? list.filter(x => x !== c) : [...list, c])}
                  className={`px-2.5 py-1 text-xs rounded-full border transition ${
                    on ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  {choiceLabel(field, c)}
                </button>
              )
            })}
            {list.length === 0 && <span className="text-xs text-gray-400">(leer = alle)</span>}
          </div>
          {resetBtn}
        </div>
      )
    }

    if (field.type === 'str' && field.choices) {
      return (
        <div className="flex items-center gap-2">
          <select
            value={String(value ?? '')}
            onChange={e => onUpdateOverride?.(field.key, e.target.value)}
            className={`px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
            }`}
          >
            {field.choices.map(c => <option key={c} value={c}>{choiceLabel(field, c)}</option>)}
          </select>
          {resetBtn}
        </div>
      )
    }

    if (field.type === 'float' || field.type === 'int') {
      const numeric = typeof value === 'number' ? value : Number(value) || 0
      return (
        <div className="flex items-center gap-2">
          {field.type === 'float' ? (
            <FloatInput
              value={typeof value === 'number' ? value : null}
              onChange={v => onUpdateOverride?.(field.key, v ?? 0)}
              className={`w-24 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
              }`}
            />
          ) : (
            <input
              type="number"
              value={numeric}
              min={field.min_value ?? undefined}
              max={field.max_value ?? undefined}
              onChange={e => onUpdateOverride?.(field.key, Number(e.target.value))}
              className={`w-24 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
              }`}
            />
          )}
          {resetBtn}
        </div>
      )
    }

    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={e => onUpdateOverride?.(field.key, e.target.value)}
          className={`w-48 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
            overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
          }`}
        />
        {resetBtn}
      </div>
    )
  }

  const renderRow = (key: string) => {
    const field = fieldsByKey[key]
    if (!field) return null
    return (
      <SettingRow
        key={key}
        label={field.label}
        description={field.description}
        readonly={editable && !field.editable}
      >
        {renderField(field)}
      </SettingRow>
    )
  }

  if (data.categories.length === 0 && extraSections.length === 0) {
    return (
      <div className="p-10 text-center">
        <Settings className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">Keine Einstellungen freigegeben</p>
        <p className="text-sm text-gray-400 mt-1">
          Welche Einstellungen das HR-Modul sehen und ändern darf, legt der Superadmin fest.
        </p>
      </div>
    )
  }

  // Client-seitig resilient: Karten ohne definierte Felder entfallen (Server
  // filtert bereits — das schützt vor Drift bei halben Updates)
  const renderableCategories = data.categories
    .map(cat => ({ ...cat, keys: cat.keys.filter(k => fieldsByKey[k]) }))
    .filter(cat => cat.keys.length > 0)
  if (renderableCategories.length === 0 && extraSections.length === 0) {
    return (
      <div className="p-10 text-center">
        <Settings className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">Keine Einstellungen freigegeben</p>
        <p className="text-sm text-gray-400 mt-1">
          Welche Einstellungen das HR-Modul sehen und ändern darf, legt der Superadmin fest.
        </p>
      </div>
    )
  }

  const navItems = [
    ...renderableCategories.map(c => c.name),
    ...extraSections.map(s => s.name),
  ]

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex flex-1 min-h-0">
        <div className="w-56 border-r bg-gray-50/50 p-3 space-y-0.5 flex-shrink-0 overflow-y-auto hidden lg:block">
          {navItems.map(name => (
            <button
              key={name}
              onClick={() => scrollToCard(name)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition text-left ${
                activeCategory === name
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ShieldCheck className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 truncate">{name}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl">
            {renderableCategories.map(cat => (
              <div key={cat.name} className="bg-white rounded-lg border p-6 mb-6" data-subgroup={cat.name}>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <Settings className="w-4 h-4 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{cat.leitfrage || cat.name}</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">{cat.intro}</p>
                <div>{cat.keys.map(renderRow)}</div>
              </div>
            ))}
            {extraSections.map(sec => (
              <div key={sec.name} data-subgroup={sec.name}>{sec.node}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
